import React, { useContext, useRef, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import {
	updateQuote,
	createActivityQuoteDB,
	updatePersonalInfoQuote,
	sendEmail,
	getToken,
	getApplicationCorrespondence,
} from '../API/Model';
import Navigation from '../Overlays/Navigation';
import PageTitle from 'components/Additional/PageElements/PageTitle/PageTitle';
import Footer from 'components/Additional/Footer/Footer';
import SfiLoader from 'components/Additional/SfiLoader/SfiLoader';
import YourInfoFormContent from 'components/Additional/YourInfoForm/YourInfoFormContent';
import { AppContext } from 'context/app-context/AppContext';
import useToast from 'components/Additional/Toast/useToast';

import { getCreatedQuotesNumbers } from 'tools/LocalStorageHelper';

import 'components/styles/styles.css';
import styles from './YourInfo_page.module.css';

function YourInfo() {
	const PAGE_NAME = 'YourInfo';

	let history = useHistory();
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const selectedQuoteNumber = params.get('selectedQuoteNumber');

	const { fetchQuoteAndLog } = useContext(AppContext);
	const { showToast } = useToast();
	const [loading, setLoading] = useState(false);
	const [quoteStatus, setQuoteStatus] = useState(null);
	const [fetchedQuotes, setFetchedQuotes] = useState([]);

	const contactEmail = useRef(localStorage.getItem('brand_email'));

	const methods = useForm({
		defaultValues: {
			first: '',
			mi: '',
			last: '',
			email: '',
			phone: '',
			birth: '',
			purchaseDate: '',
			contactTime: '0',
			consentAccepted: false,
		},
		mode: 'onChange',
	});

	const { handleSubmit, getValues } = methods;

	const getTemplateId = (quoteType) => {
		switch (quoteType) {
			case 'HO3':
				return 'HO3-Quote';
			case 'HO4':
				return 'HO4-Quote';
			case 'HO5':
				return 'HO5-Quote';
			case 'HO6':
				return 'HO6-Quote';
			case 'DF1':
				return 'DF1-Quote';
			case 'DF3-DO':
				return 'DF3-Quote';
			case 'DF3-DL':
				return 'DF3-DL-Quote';
		}
	};

	async function databaseUpdate() {
		const { first, last, email } = getValues();
		const data = {
			Insured_First_Name: first,
			Insured_Last_Name: last,
			Email: email,
		};
		await updatePersonalInfoQuote(data);

		createActivityQuoteDB('YourInfo', 'NAV', 'Continue button');
	}

	function convertDateFormat(dateStr) {
		const [month, day, year] = dateStr.split('/');
		return {
			updateQuoteFormatValue: `${year}${month}${day}`,
			updateCustomerFormatValue: `${year}-${month}-${day}`,
		};
	}

	const getCorrespondence = async (quoteNumber, quoteType) => {
		createActivityQuoteDB(
			'YourInfo',
			'API_REQ',
			'Get ApplicationCorrespondenceToken request'
		);

		const token = await getToken(JSON.stringify({}));
		const isTokenFetched = token !== 'ERROR';

		createActivityQuoteDB(
			'YourInfo',
			'API_RES',
			`Get ApplicationCorrespondenceToken response - ${
				isTokenFetched ? 'SUCCESS' : 'ERROR'
			}`
		);

		if (!isTokenFetched) return;

		createActivityQuoteDB(
			'YourInfo',
			'API_REQ',
			'Get ApplicationCorrespondence request'
		);

		const docUrl = await getApplicationCorrespondence(
			JSON.stringify({
				ApplicationNumber: quoteNumber,
				CorrespondenceTemplateId: getTemplateId(quoteType),
				Token: token,
			})
		);

		const isUrlFetched = docUrl !== 'ERROR';

		createActivityQuoteDB(
			'YourInfo',
			'API_RES',
			`Get ApplicationCorrespondence response - ${
				isUrlFetched ? 'SUCCESS' : 'ERROR'
			}`
		);

		if (!isUrlFetched) return;

		localStorage.setItem('updatedUserDataPdfUrl', docUrl);
	};

	const sendDataByEmail = async (personalInfo, quoteNumber) => {
		const formData = new FormData();
		const pdfUrl = localStorage.getItem('updatedUserDataPdfUrl');
		if (pdfUrl) formData.append('pdf_url', pdfUrl);
		formData.append('email', personalInfo.email);
		formData.append('subject', 'Your Homeowners Insurance Quote is Ready!');
		formData.append('first_name', personalInfo.first);
		formData.append('middle_name', personalInfo.mi);
		formData.append('last_name', personalInfo.last);
		formData.append('type', 'email_a');
		formData.append('contact_email', contactEmail.current);
		formData.append('phone', window.APP_CONFIG.CONTACT_PHONE);
		formData.append('customer_phone', personalInfo.phone);
		formData.append('customer_contact_time', personalInfo.contactTime);
		formData.append('customer_email', personalInfo.email);
		formData.append('quote_number', quoteNumber);
		formData.append('birth_date', personalInfo.birth);
		formData.append('business_source', window.APP_CONFIG.BUSINESS_SOURCE);
		await sendEmail(formData);
		localStorage.setItem('status', 'COMP');
	};

	const updateQuoteAndSendEmail = async (quote, emailSent, formData) => {
		const quoteNumber = quote.DTOApplication[0].ApplicationNumber;
		const quoteType = quote.DTOApplication[0].DTOBasicPolicy[0].SubTypeCd;
		const birthDt = convertDateFormat(formData.birth);
		const purchaseDt = convertDateFormat(formData.purchaseDate);
		const insuredNameEntity =
			quote.DTOApplication[0].DTOInsured[0].PartyInfo[0].NameInfo.find(
				(e) => e.NameTypeCd === 'InsuredName'
			);
		insuredNameEntity.GivenName = formData.first;
		insuredNameEntity.OtherGivenName = formData.mi;
		insuredNameEntity.Surname = formData.last;
		insuredNameEntity.CommercialName = formData.first + ' ' + formData.last;
		quote.DTOApplication[0].DTOInsured[0].PartyInfo[0].EmailInfo[0].EmailAddr =
			formData.email;
		quote.DTOApplication[0].DTOInsured[0].PartyInfo[0].PhoneInfo[0].PhoneNumber =
			formData.phone;
		quote.DTOApplication[0].DTOInsured[0].PartyInfo[0].PersonInfo[0].BirthDt =
			birthDt.updateQuoteFormatValue;
		quote.DTOApplication[0].DTOBasicPolicy[0].PurchaseDt =
			purchaseDt.updateQuoteFormatValue;
		const quoteInformationString = JSON.stringify(quote);
		const response = await updateQuote(quoteInformationString);
		setQuoteStatus((prev) => ({
			...prev,
			[quoteNumber]: response,
		}));
		if (
			response !==
				'Service temporarily unavailable. Please try again later.' &&
			response !== 'Error'
		) {
			databaseUpdate();
			if (!emailSent && quoteNumber === selectedQuoteNumber) {
				const updatedUserDataPdfUrl = localStorage.getItem(
					'updatedUserDataPdfUrl'
				);
				if (updatedUserDataPdfUrl === null && quoteType !== 'DF3-DL')
					await getCorrespondence(quoteNumber, quoteType);
				await sendDataByEmail(formData, quoteNumber);
				return true;
			}
		}
		return emailSent;
	};

	useEffect(() => {
		if (!quoteStatus) return;

		const statuses = Object.values(quoteStatus);
		const initialQuoteNumber = localStorage.getItem('quote_number');
		const additionallyCreatedQuoteNumbers = getCreatedQuotesNumbers();
		const createdQuoteNumbers = !!additionallyCreatedQuoteNumbers?.length
			? [initialQuoteNumber].concat(additionallyCreatedQuoteNumbers)
			: [initialQuoteNumber];

		if (statuses.length !== createdQuoteNumbers.length) return;

		setLoading(false);

		const allQuotesUpdated = statuses.every(
			(status) => status === 'SUCCESS'
		);

		if (allQuotesUpdated) {
			history.push('/ThankYou');
		} else if (statuses[0] != null) {
			showToast(statuses[0], 'Error');
			setQuoteStatus(null);
		}
	}, [quoteStatus]);

	useEffect(() => {
		async function fetchData() {
			const additionallyCreatedQuoteNumbers = getCreatedQuotesNumbers();
			const createdQuoteNumbers = [
				localStorage.getItem('quote_number'),
			].concat(additionallyCreatedQuoteNumbers);
			for (const cqn of createdQuoteNumbers) {
				const response = await fetchQuoteAndLog(PAGE_NAME, cqn);
				setFetchedQuotes((prev) => {
					prev.push({
						ExternalValidateBeanExist: 'No',
						DTOApplication: [response],
					});
					return prev;
				});
			}
		}

		createActivityQuoteDB('YourInfo', 'PL', 'YourInfo page loaded');
		localStorage.setItem('current_page', 'YourInfo');

		(async () => {
			await fetchData();
		})();
	}, []);

	const onSubmit = (formData) => {
		console.log(formData);

		setLoading(true);
		let emailSent = false;
		(async () => {
			for (const quote of fetchedQuotes) {
				emailSent = await updateQuoteAndSendEmail(
					quote,
					emailSent,
					formData
				);
			}
		})();
	};

	return (
		<FormProvider {...methods}>
			<div className="base-wrapper">
				<div className="abs-wrapper">
					<Navigation page={4} progress={4} hide />
				</div>

				{loading ? (
					<div className="internal_loader">
						<SfiLoader />
					</div>
				) : (
					<div className={styles.yourinfo_text_wrapper}>
						<PageTitle titleContent={<>Tell us about yourself</>} />

						<form onSubmit={handleSubmit(onSubmit)}>
							<YourInfoFormContent />
						</form>
						<Footer />
					</div>
				)}
			</div>
		</FormProvider>
	);
}

export default YourInfo;
